import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import SaaSLayout from './SaaSLayout';

// Plugin Download Tab Component - Clean, simple design
const PluginDownloadTab = ({ user, setMessage }) => {
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPlugins = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('plugin_info')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPlugins(data || []);
    } catch (error) {
      setMessage('Error fetching plugins: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlugins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = (plugin) => {
    window.open(plugin.google_drive_link, '_blank');
  };

  const latestPlugin = plugins.find(p => p.is_latest) || plugins[0];
  const olderPlugins = plugins.filter(p => !p.is_latest);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-4">Loading plugins...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Latest Version */}
      {latestPlugin && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900">Latest Version</h3>
            <p className="text-sm text-slate-500 mt-1">Download the most recent plugin release</p>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  Version {latestPlugin.version}
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">Latest</span>
                </h4>
                <p className="text-gray-600 mt-1">{latestPlugin.description || 'No description available'}</p>
              </div>
            </div>
            
            {latestPlugin.changelog && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">What's new:</p>
                <p className="text-sm text-gray-600 whitespace-pre-line">{latestPlugin.changelog}</p>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Released on {new Date(latestPlugin.created_at).toLocaleDateString()}
              </p>
              <button
                onClick={() => handleDownload(latestPlugin)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Older Versions */}
      {olderPlugins.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900">Previous Versions</h3>
            <p className="text-sm text-slate-500 mt-1">Access older plugin releases if needed</p>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {olderPlugins.map((plugin) => (
                <div key={plugin.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-900">Version {plugin.version}</p>
                    <p className="text-sm text-gray-500">
                      {plugin.description || 'Released on ' + new Date(plugin.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDownload(plugin)}
                    className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm"
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* No plugins message */}
      {plugins.length === 0 && !loading && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <p className="text-gray-500">No plugin versions available yet.</p>
        </div>
      )}
    </div>
  );
};

// Plugin Manager Tab Component - Clean admin interface
const PluginManagerTab = ({ setMessage }) => {
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPlugin, setEditingPlugin] = useState(null);
  const [formData, setFormData] = useState({
    version: '',
    description: '',
    changelog: '',
    google_drive_link: '',
    is_latest: true
  });

  useEffect(() => {
    fetchPlugins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlugins = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('plugin_info')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPlugins(data || []);
    } catch (error) {
      setMessage('Error fetching plugins: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveLoading(true);

    try {
      if (formData.is_latest) {
        await supabase
          .from('plugin_info')
          .update({ is_latest: false })
          .neq('id', editingPlugin ? editingPlugin.id : 0);
      }

      if (editingPlugin) {
        // Update existing plugin
        const { error } = await supabase
          .from('plugin_info')
          .update({
            version: formData.version,
            description: formData.description,
            changelog: formData.changelog,
            google_drive_link: formData.google_drive_link,
            is_latest: formData.is_latest
          })
          .eq('id', editingPlugin.id);

        if (error) throw error;
        setMessage('Plugin version updated successfully!');
      } else {
        // Create new plugin
        const { error } = await supabase
          .from('plugin_info')
          .insert({
            version: formData.version,
            description: formData.description,
            changelog: formData.changelog,
            google_drive_link: formData.google_drive_link,
            is_latest: formData.is_latest
          });

        if (error) throw error;
        setMessage('Plugin version added successfully!');
      }

      setFormData({
        version: '',
        description: '',
        changelog: '',
        google_drive_link: '',
        is_latest: true
      });
      setShowForm(false);
      setEditingPlugin(null);
      fetchPlugins();
    } catch (error) {
      setMessage('Error saving plugin: ' + error.message);
    } finally {
      setSaveLoading(false);
    }
  };

  const startEdit = (plugin) => {
    setEditingPlugin(plugin);
    setFormData({
      version: plugin.version,
      description: plugin.description || '',
      changelog: plugin.changelog || '',
      google_drive_link: plugin.google_drive_link,
      is_latest: plugin.is_latest
    });
    setShowForm(true);
  };

  const cancelEdit = () => {
    setEditingPlugin(null);
    setFormData({
      version: '',
      description: '',
      changelog: '',
      google_drive_link: '',
      is_latest: true
    });
    setShowForm(false);
  };

  const toggleLatest = async (pluginId, currentIsLatest) => {
    try {
      if (!currentIsLatest) {
        await supabase
          .from('plugin_info')
          .update({ is_latest: false })
          .neq('id', pluginId);
      }

      await supabase
        .from('plugin_info')
        .update({ is_latest: !currentIsLatest })
        .eq('id', pluginId);

      fetchPlugins();
      setMessage('Latest version updated.');
    } catch (error) {
      setMessage('Error updating: ' + error.message);
    }
  };

  const deletePlugin = async (pluginId) => {
    if (!window.confirm('Delete this version?')) return;

    try {
      const { error } = await supabase
        .from('plugin_info')
        .delete()
        .eq('id', pluginId);

      if (error) throw error;
      setMessage('Version deleted.');
      fetchPlugins();
    } catch (error) {
      setMessage('Error deleting: ' + error.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Plugin Versions</h3>
            <p className="text-sm text-slate-500 mt-1">Manage plugin releases and downloads</p>
          </div>
          <button
            onClick={() => {
              if (editingPlugin) {
                cancelEdit();
              } else {
                setShowForm(!showForm);
              }
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {editingPlugin ? 'Cancel Edit' : 'Add Version'}
          </button>
        </div>
      </div>

      {/* Add Form (Collapsible) */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Version *</label>
                  <input
                    type="text"
                    required
                    value={formData.version}
                    onChange={(e) => setFormData({...formData, version: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 1.0.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Google Drive Link *</label>
                  <input
                    type="url"
                    required
                    value={formData.google_drive_link}
                    onChange={(e) => setFormData({...formData, google_drive_link: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://drive.google.com/..."
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Changelog</label>
                <textarea
                  value={formData.changelog}
                  onChange={(e) => setFormData({...formData, changelog: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="What's new in this version..."
                />
              </div>
              
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_latest}
                    onChange={(e) => setFormData({...formData, is_latest: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Set as latest version</span>
                </label>
              </div>
              
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saveLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saveLoading ? 'Saving...' : editingPlugin ? 'Update Version' : 'Save Version'}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Plugins List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading versions...</p>
            </div>
          ) : plugins.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No plugin versions yet. Click "Add Version" to create one.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Version</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {plugins.map((plugin) => (
                  <tr key={plugin.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">v{plugin.version}</span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-600">{plugin.description || '-'}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(plugin.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {plugin.is_latest ? (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Latest</span>
                      ) : (
                        <button
                          onClick={() => toggleLatest(plugin.id, plugin.is_latest)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Set as latest
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={plugin.google_drive_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                        >
                          View
                        </a>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => startEdit(plugin)}
                          className="text-green-600 hover:text-green-800"
                        >
                          Edit
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => deletePlugin(plugin.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// Image Popup Component
const ImagePopup = ({ src, alt, onClose }) => {
  if (!src) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 z-50 overflow-auto"
      onClick={onClose}
    >
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="relative max-w-5xl w-full">
          <img 
            src={src} 
            alt={alt}
            className="w-full h-auto object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-white bg-black bg-opacity-60 rounded-full p-2 hover:bg-opacity-80 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="absolute bottom-2 left-2 text-white bg-black bg-opacity-60 px-3 py-1 rounded text-sm">
            Click outside image or ✕ to close
          </div>
        </div>
      </div>
    </div>
  );
};

function Dashboard({ user, isAdmin }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [licenseKey, setLicenseKey] = useState('');
  const [tokens, setTokens] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [userLicenses, setUserLicenses] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userFilter, setUserFilter] = useState('all'); // 'all', 'verified', 'unverified', 'licensed'
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // License generation form (admin only)
  const [generateCount, setGenerateCount] = useState(1);
  const [expiryDays, setExpiryDays] = useState(365);
  const [selectedTier, setSelectedTier] = useState('Solo');

  // Settings
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Image popup state
  const [popupImage, setPopupImage] = useState(null);
  
  // License assignment state
  const [assignmentEmail, setAssignmentEmail] = useState('');
  const [assigningLicense, setAssigningLicense] = useState(null);
  
  // License expansion state
  const [expandedLicenses, setExpandedLicenses] = useState({});
  
  // User search state
  const [userSearchQuery, setUserSearchQuery] = useState('');

  useEffect(() => {
    loadUserTokens();
    loadUserLicenses();
    if (isAdmin) {
      loadLicenses();
      loadPurchaseRequests();
      loadAllUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  const loadUserTokens = async () => {
    const { data } = await supabase
      .from('tokens')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    setTokens(data || []);
  };

  const loadUserLicenses = async () => {
    const { data } = await supabase
      .from('licenses')
      .select(`
        *,
        license_tiers (
          name,
          max_websites
        )
      `)
      .eq('redeemed_by', user.id)
      .not('redeemed_by', 'is', null)
      .order('created_at', { ascending: false });
    setUserLicenses(data || []);
  };

  const loadLicenses = async () => {
    const { data } = await supabase
      .from('licenses')
      .select(`
        *,
        license_tiers (
          name,
          max_websites
        )
      `)
      .order('created_at', { ascending: false });
    setLicenses(data || []);
  };

  const loadPurchaseRequests = async () => {
    const { data } = await supabase
      .from('purchase_requests')
      .select('*')
      .order('created_at', { ascending: false });
    setPurchaseRequests(data || []);
  };

  const loadAllUsers = async () => {
    try {
      // Get ALL auth users (including those without licenses)
      const { data: allAuthUsers, error: authError } = await supabase.rpc('get_all_auth_users');
      
      if (authError) {
        console.error('Error fetching auth users:', authError);
        // Fallback to old method if RPC fails
        loadAllUsersOldMethod();
        return;
      }

      // Get ALL licenses (with user data)
      const { data: allLicenses } = await supabase
        .from('licenses')
        .select(`
          id,
          redeemed_by,
          user_email,
          license_key,
          status,
          redeemed_at,
          expires_at,
          license_tiers (
            name,
            max_websites
          )
        `)
        .order('redeemed_at', { ascending: false });

      // Get ALL tokens
      const { data: allTokens } = await supabase
        .from('tokens')
        .select('*')
        .order('created_at', { ascending: false });

      // Create a comprehensive user list that includes:
      // 1. All auth users (even those without licenses)
      // 2. All licensed users (even if not in auth users)
      const userMap = new Map();

      // Add all auth users first
      allAuthUsers.forEach(authUser => {
        userMap.set(authUser.id, {
          id: authUser.id,
          email: authUser.email,
          created_at: authUser.created_at,
          email_verified: authUser.email_confirmed_at !== null,
          verified_at: authUser.email_confirmed_at,
          has_license: false,
          licenses: [],
          tokens: [],
          totalTokens: 0,
          activeTokens: 0,
          websites: []
        });
      });

      // Add/update with license data
      allLicenses?.forEach(license => {
        if (license.redeemed_by) {
          const userId = license.redeemed_by;
          
          if (!userMap.has(userId)) {
            // User has license but not in auth (shouldn't happen but handle it)
            userMap.set(userId, {
              id: userId,
              email: license.user_email,
              created_at: license.redeemed_at,
              email_verified: true, // Assume verified if they have license
              verified_at: license.redeemed_at,
              has_license: false,
              licenses: [],
              tokens: [],
              totalTokens: 0,
              activeTokens: 0,
              websites: []
            });
          }
          
          const user = userMap.get(userId);
          user.has_license = true;
          user.licenses.push(license);
        }
      });

      // Add token data
      allTokens?.forEach(token => {
        if (token.user_id && userMap.has(token.user_id)) {
          const user = userMap.get(token.user_id);
          user.tokens.push(token);
        }
      });

      // Calculate token stats and websites for each user
      userMap.forEach(user => {
        user.totalTokens = user.tokens.length;
        user.activeTokens = user.tokens.filter(t => t.status === 'active').length;
        user.websites = [...new Set(user.tokens.filter(t => t.website_domain).map(t => t.website_domain))];
      });

      const combinedUsers = Array.from(userMap.values()).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );

      setAllUsers(combinedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      loadAllUsersOldMethod(); // Fallback
    }
  };

  // Keep old method as fallback
  const loadAllUsersOldMethod = async () => {
    try {
      const { data: usersWithLicenses } = await supabase
        .from('licenses')
        .select(`
          id,
          redeemed_by,
          user_email,
          license_key,
          status,
          redeemed_at,
          expires_at,
          email_verified,
          verified_at,
          verified_by,
          license_tiers (
            name,
            max_websites
          )
        `)
        .not('redeemed_by', 'is', null);

      const userIds = [...new Set(usersWithLicenses?.map(l => l.redeemed_by) || [])];
      
      if (userIds.length === 0) {
        setAllUsers([]);
        return;
      }

      const { data: allTokens } = await supabase
        .from('tokens')
        .select('*')
        .in('user_id', userIds);

      const combinedUsers = userIds.map(userId => {
        const userLicenses = usersWithLicenses?.filter(l => l.redeemed_by === userId) || [];
        const userTokens = allTokens?.filter(t => t.user_id === userId) || [];
        const userEmail = userLicenses.find(l => l.user_email)?.user_email || 
                         (userId === user.id ? user.email : `User ${userId.slice(-8)}`);
        
        return {
          id: userId,
          email: userEmail,
          created_at: userLicenses[0]?.redeemed_at,
          email_verified: userLicenses[0]?.email_verified || false,
          verified_at: userLicenses[0]?.verified_at,
          verified_by: userLicenses[0]?.verified_by,
          has_license: true,
          licenses: userLicenses,
          tokens: userTokens,
          totalTokens: userTokens.length,
          activeTokens: userTokens.filter(t => t.status === 'active').length,
          websites: [...new Set(userTokens.filter(t => t.website_domain).map(t => t.website_domain))]
        };
      });

      setAllUsers(combinedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      setAllUsers([]);
    }
  };

  const redeemLicense = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Redemption goes through the redeem-license edge function, which runs
      // with service_role. Doing it from the browser required a policy that
      // let any anon caller read and write every licence row - that policy is
      // now dropped, and an unredeemed licence is deliberately invisible to
      // RLS (both owner columns are NULL), so it could not be looked up here
      // anyway. The function also enforces "already claimed" server-side,
      // where it cannot be bypassed.
      const { data, error: fnError } = await supabase.functions.invoke(
        'redeem-license',
        { body: { license_key: licenseKey.trim().toUpperCase() } }
      );

      if (fnError) {
        // Non-2xx from the function: surface its message, not a generic one.
        let detail = '';
        try {
          detail = (await fnError.context?.json())?.error || '';
        } catch {
          // response body was not JSON - fall through to the generic message
        }
        throw new Error(detail || 'Could not redeem that licence key.');
      }
      if (data?.error) throw new Error(data.error);

      setMessage('License redeemed successfully!');
      setLicenseKey('');
      loadUserTokens();
      loadUserLicenses();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const generateToken = async () => {
    setLoading(true);
    setMessage('');

    try {
      // Check if user has redeemed licenses
      const { data: redeemedLicenses, error: checkError } = await supabase
        .from('licenses')
        .select(`
          *,
          license_tiers (
            name,
            max_websites
          )
        `)
        .eq('redeemed_by', user.id)
        .eq('status', 'active');

      if (checkError) {
        console.error('Error checking licenses:', checkError);
        throw new Error('Error checking your licenses: ' + checkError.message);
      }

      if (!redeemedLicenses || redeemedLicenses.length === 0) {
        throw new Error('You need to redeem a license first');
      }

      // Check token limits based on license tier
      const userLicense = redeemedLicenses[0];
      const tierName = userLicense.license_tiers?.name || 'Solo';
      const maxTokens = userLicense.license_tiers?.max_websites || 5;
      
      // Count existing tokens for this license
      const { data: existingTokens } = await supabase
        .from('tokens')
        .select('*')
        .eq('license_id', userLicense.id)
        .eq('status', 'active');

      if (existingTokens && existingTokens.length >= maxTokens && tierName !== 'Unlimited') {
        throw new Error(`You have reached the maximum number of tokens (${maxTokens}) for your ${tierName} license`);
      }

      // Generate token
      const token = 'cb_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      
      const { error: insertError } = await supabase
        .from('tokens')
        .insert({
          user_id: user.id,
          token: token,
          license_id: redeemedLicenses[0].id, // Link to the first redeemed license
          status: 'active',
          expires_at: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() // 1 year
        });

      if (insertError) {
        console.error('Error creating token:', insertError);
        throw new Error('Failed to generate token: ' + insertError.message);
      }

      setMessage('Token generated successfully!');
      loadUserTokens();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const generateLicenses = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Get tier ID for selected tier
      const { data: tierData } = await supabase
        .from('license_tiers')
        .select('id')
        .eq('name', selectedTier)
        .single();

      if (!tierData) {
        throw new Error('Invalid license tier selected');
      }

      const licensesToCreate = [];
      const expiryDate = new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000);

      for (let i = 0; i < generateCount; i++) {
        const licenseKey = 'CB-' + Math.random().toString(36).substring(2, 6).toUpperCase() + 
                          '-' + Math.random().toString(36).substring(2, 6).toUpperCase() +
                          '-' + Math.random().toString(36).substring(2, 6).toUpperCase();
        
        licensesToCreate.push({
          license_key: licenseKey,
          tier_id: tierData.id,
          expires_at: expiryDate.toISOString()
        });
      }

      const { error } = await supabase
        .from('licenses')
        .insert(licensesToCreate);

      if (error) throw error;

      setMessage(`Successfully generated ${generateCount} licenses!`);
      setGenerateCount(1);
      loadLicenses();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (id, status) => {
    const { error } = await supabase
      .from('purchase_requests')
      .update({ status })
      .eq('id', id);

    if (error) {
      setMessage('Error updating request');
    } else {
      setMessage(`Request ${status} successfully!`);
      loadPurchaseRequests();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'license_sent': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-indigo-100 text-indigo-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const updatePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (newPassword !== confirmPassword) {
      setMessage('New passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      setMessage('Password updated successfully!');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setMessage('Error updating password: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const assignLicense = async (licenseId, email) => {
    setLoading(true);
    setMessage('');

    try {
      // Update license with manual assignment
      const { error } = await supabase
        .from('licenses')
        .update({
          user_email: email,
          assigned_at: new Date().toISOString(),
          status: 'assigned' // New status for manually assigned licenses
        })
        .eq('id', licenseId);

      if (error) throw error;

      setMessage(`License assigned to ${email} successfully!`);
      setAssignmentEmail('');
      setAssigningLicense(null);
      loadLicenses();
    } catch (error) {
      setMessage('Error assigning license: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleLicenseExpansion = (licenseId) => {
    setExpandedLicenses(prev => ({
      ...prev,
      [licenseId]: !prev[licenseId]
    }));
  };

  const filteredUsers = allUsers
    .filter(user => {
      // Apply search filter
      const matchesSearch = user.email.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        user.websites.some(website => website.toLowerCase().includes(userSearchQuery.toLowerCase())) ||
        user.id.toLowerCase().includes(userSearchQuery.toLowerCase());
      
      if (!matchesSearch) return false;
      
      // Apply status filter
      switch (userFilter) {
        case 'verified':
          return user.email_verified === true;
        case 'unverified':
          return user.email_verified === false;
        case 'licensed':
          return user.has_license === true;
        case 'all':
        default:
          return true;
      }
    });

  const verifyUserEmail = async (userId, userEmail) => {
    try {
      // SIMPLIFIED: Use only Supabase's built-in verification
      const { error } = await supabase.rpc('admin_verify_user_email', {
        p_user_id: userId
      });
      
      if (error) throw error;
      
      setMessage(`✅ User ${userEmail} has been verified and can now login.`);
      loadAllUsers(); // Refresh the list
    } catch (error) {
      console.error('Error verifying user:', error);
      setMessage(`❌ Error verifying user: ${error.message}`);
    }
  };

  const unverifyUserEmail = async (userId, userEmail) => {
    try {
      // SIMPLIFIED: Use only Supabase's built-in verification
      const { error } = await supabase.rpc('admin_unverify_user_email', {
        p_user_id: userId
      });
      
      if (error) throw error;
      
      setMessage(`❌ User ${userEmail} verification has been removed.`);
      loadAllUsers(); // Refresh the list
    } catch (error) {
      console.error('Error unverifying user:', error);
      setMessage(`❌ Error unverifying user: ${error.message}`);
    }
  };

  const setUserPassword = async (userId, userEmail) => {
    const newPassword = window.prompt(
      `Set a new password for ${userEmail}.\nMust be at least 6 characters.\nThey can sign in with this password immediately.`
    );
    if (newPassword === null) return;
    if (newPassword.length < 6) {
      setMessage(`❌ Password must be at least 6 characters.`);
      return;
    }
    if (!window.confirm(`Set new password for ${userEmail}? Send the password to them via your own channel — it will NOT be emailed.`)) {
      return;
    }

    try {
      const { error } = await supabase.rpc('admin_set_user_password', {
        p_user_id: userId,
        p_new_password: newPassword
      });
      if (error) throw error;
      setMessage(`✅ Password updated for ${userEmail}. Share it with them securely.`);
    } catch (error) {
      console.error('Error setting password:', error);
      setMessage(`❌ Error setting password: ${error.message}`);
    }
  };

  const getUsageStats = () => {
    const totalTokens = tokens.length;
    const activeTokens = tokens.filter(t => t.status === 'active').length;
    const domainsUsed = [...new Set(tokens.filter(t => t.website_domain).map(t => t.website_domain))];
    const currentLicense = userLicenses[0];
    const maxTokens = currentLicense?.license_tiers?.max_websites || 0;
    
    return {
      totalTokens,
      activeTokens,
      domainsUsed,
      maxTokens,
      currentLicense,
      usagePercentage: maxTokens > 0 ? Math.round((totalTokens / maxTokens) * 100) : 0
    };
  };

  // Copy to clipboard function
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage('Copied to clipboard!');
      setTimeout(() => setMessage(''), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      setMessage('Failed to copy to clipboard');
    }
  };

  const stats = getUsageStats();

  return (
    <>
      <SaaSLayout 
        user={user} 
        isAdmin={isAdmin} 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        signOut={signOut}
      >
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${message.includes('Error') || message.includes('Invalid') || message.includes('do not match') ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'}`}>
          <div className="flex items-center">
            <div className="mr-3">
              {message.includes('Error') || message.includes('Invalid') || message.includes('do not match') ? (
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            {message}
          </div>
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded-xl">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-600">Active Tokens</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.activeTokens}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-3 bg-gradient-to-r from-green-100 to-green-200 rounded-xl">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9-9a9 9 0 00-9 9m9 9c0 1.657.343 3.236.96 4.666a9.004 9.004 0 01-.96-4.666zm0 0c1.657 0 3.236.343 4.666.96A9.004 9.004 0 0121 12z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-600">Websites</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.domainsUsed.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-3 bg-gradient-to-r from-purple-100 to-purple-200 rounded-xl">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-600">Usage</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.usagePercentage}%</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-3 bg-gradient-to-r from-amber-100 to-amber-200 rounded-xl">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-600">License Tier</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.currentLicense?.license_tiers?.name || 'None'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* License Overview */}
          {stats.currentLicense && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">License Overview</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">License Details</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span><span className="font-medium">License Key:</span></span>
                        <div className="flex items-center space-x-2">
                          <code className="bg-gray-100 px-2 py-1 rounded text-sm">{stats.currentLicense.license_key}</code>
                          <button
                            onClick={() => copyToClipboard(stats.currentLicense.license_key)}
                            className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                            title="Copy license key"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <p><span className="font-medium">Tier:</span> {stats.currentLicense.license_tiers?.name}</p>
                      <p><span className="font-medium">Max Tokens:</span> {stats.currentLicense.license_tiers?.max_websites === 999999 ? 'Unlimited' : stats.currentLicense.license_tiers?.max_websites}</p>
                      <p><span className="font-medium">Activated:</span> {new Date(stats.currentLicense.redeemed_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Usage Progress</h4>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Tokens Used</span>
                          <span>{stats.totalTokens}/{stats.maxTokens === 999999 ? '∞' : stats.maxTokens}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${Math.min(stats.usagePercentage, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Active Websites */}
          {stats.domainsUsed.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Active Websites</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {stats.domainsUsed.map((domain, index) => (
                    <div key={index} className="flex items-center p-4 bg-slate-50 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
                      <div className="p-2 bg-green-100 rounded-lg mr-3">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9-9a9 9 0 00-9 9m9 9c0 1.657.343 3.236.96 4.666a9.004 9.004 0 01-.96-4.666zm0 0c1.657 0 3.236.343 4.666.96A9.004 9.004 0 0121 12z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{domain}</p>
                        <p className="text-sm text-slate-500">
                          {tokens.filter(t => t.website_domain === domain).length} token(s)
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Account Settings */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Account Settings</h3>
              <p className="text-sm text-slate-500 mt-1">Manage your account preferences and security</p>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                {/* Email (Read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>

                {/* Change Password */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Change Password</h4>
                  <form onSubmit={updatePassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter new password"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Confirm new password"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? 'Updating...' : 'Update Password'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Account Information</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Account Type</h4>
                  <p className="text-lg font-medium text-gray-900">
                    {isAdmin ? 'Administrator' : 'User'}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Account Created</h4>
                  <p className="text-lg font-medium text-gray-900">
                    {new Date(user.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Other tabs will be added incrementally */}
      {activeTab === 'redeem' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900">Redeem License Key</h3>
            <p className="text-sm text-slate-500 mt-1">Enter your license key to activate your Content Boom subscription</p>
          </div>
          <div className="p-6">
            <form onSubmit={redeemLicense} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">License Key</label>
                <input
                  type="text"
                  placeholder="CB-XXXX-XXXX-XXXX"
                  value={licenseKey}
                  onChange={(e) => setLicenseKey(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Redeeming...' : 'Redeem License'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Tokens Tab */}
      {activeTab === 'tokens' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">My Access Tokens</h3>
              <p className="text-sm text-slate-500 mt-1">Manage your website activation tokens</p>
            </div>
            <button
              onClick={generateToken}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Generating...' : 'Generate New Token'}
            </button>
          </div>
          <div className="p-6">
            {tokens.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <p className="text-slate-500 mb-2 font-medium">No tokens generated yet</p>
                <p className="text-sm text-slate-400">Redeem a license first, then generate tokens for your websites</p>
              </div>
            ) : (
              <div className="space-y-4">
                {tokens.map(token => (
                  <div key={token.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-mono text-sm bg-gray-100 px-3 py-2 rounded break-all flex-1 mr-2">
                        {token.token}
                      </div>
                      <button
                        onClick={() => copyToClipboard(token.token)}
                        className="p-2 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
                        title="Copy token"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p><span className="font-medium">Status:</span> 
                        <span className={`ml-2 px-2 py-1 rounded text-xs ${
                          token.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {token.status}
                        </span>
                      </p>
                      {token.website_domain && (
                        <p><span className="font-medium">Website:</span> {token.website_domain}</p>
                      )}
                      <p><span className="font-medium">Created:</span> {new Date(token.created_at).toLocaleDateString()}</p>
                      {token.expires_at && (
                        <p><span className="font-medium">Expires:</span> {new Date(token.expires_at).toLocaleDateString()}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin: Generate Licenses */}
      {activeTab === 'generate' && isAdmin && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Generate Licenses</h3>
            <p className="text-sm text-gray-500 mt-1">Create new license keys for customers</p>
          </div>
          <div className="p-6">
            <form onSubmit={generateLicenses} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">License Tier</label>
                  <select
                    value={selectedTier}
                    onChange={(e) => setSelectedTier(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Solo">Solo (5 tokens)</option>
                    <option value="Studio">Studio (25 tokens)</option>
                    <option value="Agency">Agency (100 tokens)</option>
                    <option value="Unlimited">Unlimited (∞ tokens)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Number of Licenses</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={generateCount}
                    onChange={(e) => setGenerateCount(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Duration</label>
                  <select
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={365}>1 Year</option>
                    <option value={1095}>3 Years</option>
                    <option value={1825}>5 Years</option>
                    <option value={36500}>Lifetime</option>
                  </select>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Generating...' : `Generate ${generateCount} License${generateCount > 1 ? 's' : ''}`}
              </button>
            </form>

            {licenses.length > 0 && (
              <div className="mt-8">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Generated Licenses ({licenses.length})</h4>
                <div className="max-h-96 overflow-y-auto space-y-3">
                  {licenses.map(license => {
                    const isExpanded = expandedLicenses[license.id];
                    return (
                      <div key={license.id} className="bg-white border border-gray-200 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md">
                        {/* Compact Header - Always Visible */}
                        <div 
                          className="flex justify-between items-center p-4 cursor-pointer"
                          onClick={() => toggleLicenseExpansion(license.id)}
                        >
                          <div className="flex items-center space-x-3 flex-1">
                            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{license.license_key}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(license.license_key);
                              }}
                              className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                              title="Copy license key"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              license.status === 'active' ? 'bg-green-100 text-green-800' :
                              license.status === 'redeemed' ? 'bg-blue-100 text-blue-800' :
                              license.status === 'assigned' ? 'bg-purple-100 text-purple-800' :
                              license.status === 'expired' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {license.status === 'active' && !license.redeemed_by ? 'Available' :
                               license.status === 'active' && license.redeemed_by ? 'Redeemed' :
                               license.status}
                            </span>
                            
                            {/* Expand/Collapse Icon */}
                            <svg 
                              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-gray-100">
                            {/* License Details Grid */}
                            <div className="grid grid-cols-2 gap-4 text-sm mt-4">
                              <div>
                                <span className="text-gray-500">Generated:</span>
                                <p className="font-medium">{new Date(license.created_at).toLocaleString()}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Tier:</span>
                                <p className="font-medium">{license.license_tiers?.name || 'Standard'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Max Websites:</span>
                                <p className="font-medium">{license.license_tiers?.max_websites || 1}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Expires:</span>
                                <p className="font-medium">
                                  {license.expires_at ? new Date(license.expires_at).toLocaleDateString() : 'Never'}
                                </p>
                              </div>
                            </div>

                            {/* Assignment/Redemption Info */}
                            {license.redeemed_by && (
                              <div className="mt-4 pt-4 border-t border-gray-100">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">Assigned to:</span>
                                    <p className="font-medium text-blue-600">{license.user_email || 'Unknown User'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Redeemed:</span>
                                    <p className="font-medium">
                                      {license.redeemed_at ? new Date(license.redeemed_at).toLocaleString() : 'Not yet'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Manual Assignment Section */}
                            {!license.redeemed_by && license.status === 'active' && (
                              <div className="mt-4 pt-4 border-t border-gray-100">
                                {assigningLicense === license.id ? (
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="email"
                                      value={assignmentEmail}
                                      onChange={(e) => setAssignmentEmail(e.target.value)}
                                      placeholder="Enter email to assign license"
                                      className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                    <button
                                      onClick={() => assignLicense(license.id, assignmentEmail)}
                                      disabled={!assignmentEmail || loading}
                                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      Assign
                                    </button>
                                    <button
                                      onClick={() => {
                                        setAssigningLicense(null);
                                        setAssignmentEmail('');
                                      }}
                                      className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setAssigningLicense(license.id)}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                  >
                                    ✉️ Assign to User
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin: Purchase Requests */}
      {activeTab === 'requests' && isAdmin && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Purchase Requests ({purchaseRequests.length})</h3>
            <p className="text-sm text-gray-500 mt-1">Review and approve customer purchase submissions</p>
          </div>
          <div className="p-6">
            {purchaseRequests.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-500 mb-2">No purchase requests yet</p>
                <p className="text-sm text-gray-400">Customer purchase requests will appear here for review</p>
              </div>
            ) : (
              <div className="space-y-4">
                {purchaseRequests.map(request => (
                  <div key={request.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h4 className="font-medium text-slate-900">{request.name}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                            {request.status.replace('_', ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          <span className="font-medium">Email:</span> {request.email}
                        </p>
                        <p className="text-sm text-slate-800 mb-3">
                          <span className="font-medium">Request:</span> {request.purchase_details}
                        </p>
                        
                        {/* Screenshot Display */}
                        {request.screenshot_url ? (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-slate-700 mb-2">Purchase Screenshot:</p>
                            <div 
                              className="inline-block border border-slate-200 rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                              onClick={() => setPopupImage({ src: request.screenshot_url, alt: 'Purchase Screenshot' })}
                            >
                              <img 
                                src={request.screenshot_url} 
                                alt="Purchase Screenshot Thumbnail" 
                                className="w-24 h-24 object-cover bg-slate-50 hover:opacity-90 transition-opacity"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <div className="p-2 text-center text-slate-500 hidden">
                                <svg className="w-8 h-8 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <p className="text-xs">Failed to load</p>
                              </div>
                            </div>
                            <div className="mt-2">
                              <button
                                onClick={() => setPopupImage({ src: request.screenshot_url, alt: 'Purchase Screenshot' })}
                                className="text-sm text-blue-600 hover:text-blue-800 underline"
                              >
                                🔍 Click to view full size
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center">
                              <svg className="w-5 h-5 text-amber-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                              </svg>
                              <p className="text-sm text-amber-700">No screenshot provided with this request</p>
                            </div>
                          </div>
                        )}
                        
                        <p className="text-xs text-slate-500 mb-3">
                          <span className="font-medium">Submitted:</span> {new Date(request.created_at).toLocaleString()}
                        </p>
                      </div>
                      
                      {/* Status Management */}
                      <div className="flex flex-col space-y-2 min-w-max">
                        {request.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateRequestStatus(request.id, 'approved')}
                              className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => updateRequestStatus(request.id, 'in_progress')}
                              className="px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
                            >
                              In Progress
                            </button>
                            <button
                              onClick={() => updateRequestStatus(request.id, 'rejected')}
                              className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {request.status === 'approved' && (
                          <>
                            <button
                              onClick={() => updateRequestStatus(request.id, 'license_sent')}
                              className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                            >
                              License Sent
                            </button>
                            <button
                              onClick={() => updateRequestStatus(request.id, 'pending')}
                              className="px-3 py-1.5 text-sm font-medium text-yellow-700 bg-yellow-100 rounded-lg hover:bg-yellow-200 transition-colors"
                            >
                              Back to Pending
                            </button>
                          </>
                        )}
                        {request.status === 'license_sent' && (
                          <>
                            <button
                              onClick={() => updateRequestStatus(request.id, 'completed')}
                              className="px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-100 rounded-lg hover:bg-indigo-200 transition-colors"
                            >
                              Mark Complete
                            </button>
                            <button
                              onClick={() => updateRequestStatus(request.id, 'approved')}
                              className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
                            >
                              Back to Approved
                            </button>
                          </>
                        )}
                        {(request.status === 'in_progress' || request.status === 'rejected' || request.status === 'completed') && (
                          <button
                            onClick={() => updateRequestStatus(request.id, 'pending')}
                            className="px-3 py-1.5 text-sm font-medium text-yellow-700 bg-yellow-100 rounded-lg hover:bg-yellow-200 transition-colors"
                          >
                            Reset to Pending
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin: Users & Licenses */}
      {activeTab === 'users' && isAdmin && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Users & License Management ({filteredUsers.length})</h3>
                <p className="text-sm text-slate-500 mt-1">Monitor user activity, license usage, and website deployments</p>
              </div>
            </div>
            
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search users by email, website, or user ID..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {userSearchQuery && (
                <button
                  onClick={() => setUserSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg className="h-4 w-4 text-slate-400 hover:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            
            {/* Filter Tabs */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setUserFilter('all')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  userFilter === 'all' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All Users ({allUsers.length})
              </button>
              <button
                onClick={() => setUserFilter('unverified')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  userFilter === 'unverified' 
                    ? 'bg-yellow-100 text-yellow-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Unverified ({allUsers.filter(u => !u.email_verified).length})
              </button>
              <button
                onClick={() => setUserFilter('verified')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  userFilter === 'verified' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Verified ({allUsers.filter(u => u.email_verified).length})
              </button>
              <button
                onClick={() => setUserFilter('licensed')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  userFilter === 'licensed' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Licensed ({allUsers.filter(u => u.has_license).length})
              </button>
            </div>
          </div>
          <div className="p-6">
            {allUsers.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <p className="text-slate-500 mb-2 font-medium">No active users yet</p>
                <p className="text-sm text-slate-400">Users with redeemed licenses will appear here</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <p className="text-slate-500 mb-2 font-medium">No users found</p>
                <p className="text-sm text-slate-400">Try adjusting your search criteria</p>
                <button
                  onClick={() => setUserSearchQuery('')}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredUsers.map(userData => (
                  <div key={userData.id} className="border border-slate-200 rounded-xl overflow-hidden">
                    {/* User Summary */}
                    <div 
                      className="p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => setExpandedUser(expandedUser === userData.id ? null : userData.id)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                            {userData.email[0]?.toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-slate-900">{userData.email}</h4>
                              {!userData.email_verified && (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                                  Email Pending
                                </span>
                              )}
                              {userData.email_verified && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                  Verified
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">
                              {userData.licenses.length} license(s) • {userData.totalTokens} token(s) • {userData.websites.length} website(s)
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className="flex space-x-2">
                              {userData.licenses.map(license => (
                                <span key={license.license_key} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                  {license.license_tiers?.name}
                                </span>
                              ))}
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                              Joined {new Date(userData.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <svg 
                            className={`w-5 h-5 text-slate-400 transition-transform ${expandedUser === userData.id ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Expanded User Details */}
                    {expandedUser === userData.id && (
                      <div className="p-6 bg-white border-t border-slate-200">
                        {/* Email Verification Section - Always Show */}
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="font-medium text-slate-900 mb-1">Email Verification</h5>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-slate-600">Status:</span>
                                {userData.email_verified ? (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Verified
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    Pending Verification
                                  </span>
                                )}
                              </div>
                              {userData.verified_at && (
                                <p className="text-xs text-slate-500 mt-1">
                                  Verified on {new Date(userData.verified_at).toLocaleDateString()} by {userData.verified_by}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              {userData.email_verified ? (
                                <button
                                  onClick={() => unverifyUserEmail(userData.id, userData.email)}
                                  className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                                >
                                  Unverify
                                </button>
                              ) : (
                                <button
                                  onClick={() => verifyUserEmail(userData.id, userData.email)}
                                  className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                                >
                                  Verify Email
                                </button>
                              )}
                              {isAdmin && (
                                <button
                                  onClick={() => setUserPassword(userData.id, userData.email)}
                                  className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                  title="Manually set a new password for this user (for when they can't receive reset emails)"
                                >
                                  Set Password
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Licenses */}
                          <div>
                            <h5 className="font-medium text-slate-900 mb-3 flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1721 9z" />
                              </svg>
                              Licenses ({userData.licenses.length})
                            </h5>
                            <div className="space-y-2">
                              {userData.licenses.map(license => (
                                <div key={license.license_key} className="p-3 bg-slate-50 rounded-lg">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-mono text-sm">{license.license_key}</span>
                                    <button
                                      onClick={() => copyToClipboard(license.license_key)}
                                      className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                                      title="Copy license key"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </button>
                                  </div>
                                  <div className="flex justify-between text-sm mb-2">
                                    <span className="text-slate-600">{license.license_tiers?.name} Plan</span>
                                    <span className="text-slate-500">
                                      Activated {new Date(license.redeemed_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                      <span className="text-slate-600">Email Status:</span>
                                      {license.email_verified ? (
                                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center gap-1">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                          Verified
                                        </span>
                                      ) : (
                                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full flex items-center gap-1">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                          </svg>
                                          Pending
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex gap-1">
                                      {license.email_verified ? (
                                        <button
                                          onClick={() => unverifyUserEmail(license.id, userData.email)}
                                          className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                          title="Mark as unverified"
                                        >
                                          Unverify
                                        </button>
                                      ) : (
                                        <button
                                          onClick={() => verifyUserEmail(license.id, userData.email)}
                                          className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                          title="Mark as verified"
                                        >
                                          Verify Email
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  {license.verified_at && (
                                    <div className="text-xs text-slate-400 mt-1">
                                      Verified on {new Date(license.verified_at).toLocaleDateString()} by {license.verified_by}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Tokens & Websites */}
                          <div>
                            <h5 className="font-medium text-slate-900 mb-3 flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9-9a9 9 0 00-9 9m9 9c0 1.657.343 3.236.96 4.666a9.004 9.004 0 01-.96-4.666zm0 0c1.657 0 3.236.343 4.666.96A9.004 9.004 0 0121 12z" />
                              </svg>
                              Active Websites ({userData.websites.length})
                            </h5>
                            {userData.websites.length > 0 ? (
                              <div className="space-y-2">
                                {userData.websites.map(domain => {
                                  const domainTokens = userData.tokens.filter(t => t.website_domain === domain);
                                  return (
                                    <div key={domain} className="p-3 bg-slate-50 rounded-lg">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="font-medium text-slate-900">{domain}</p>
                                          <p className="text-sm text-slate-500">{domainTokens.length} token(s)</p>
                                        </div>
                                        <div className="text-right">
                                          {domainTokens.map(token => (
                                            <div key={token.id} className="text-xs text-slate-400">
                                              {token.status === 'active' ? '🟢' : '🔴'} {token.token.slice(-8)}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-slate-500 text-sm bg-slate-50 p-3 rounded-lg">No websites activated yet</p>
                            )}
                          </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="mt-6 pt-4 border-t border-slate-200">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-slate-900">{userData.totalTokens}</div>
                              <div className="text-sm text-slate-500">Total Tokens</div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-green-600">{userData.activeTokens}</div>
                              <div className="text-sm text-slate-500">Active Tokens</div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-blue-600">{userData.websites.length}</div>
                              <div className="text-sm text-slate-500">Websites</div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-purple-600">{userData.licenses.length}</div>
                              <div className="text-sm text-slate-500">Licenses</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Plugin Download */}
      {activeTab === 'download' && (
        <PluginDownloadTab user={user} setMessage={setMessage} />
      )}

      {/* Admin: Plugin Manager */}
      {activeTab === 'plugin-manager' && isAdmin && (
        <PluginManagerTab user={user} setMessage={setMessage} />
      )}
      </SaaSLayout>
      
      {/* Image Popup */}
      {popupImage && (
        <ImagePopup 
          src={popupImage.src}
          alt={popupImage.alt}
          onClose={() => setPopupImage(null)}
        />
      )}
    </>
  );
}

export default Dashboard;